import moment from "moment";
import { Box, Grid } from "@mui/material";
import { ProductCard } from "@atoms/ProductCard";
import { Testimonials } from "@atoms/Testimonials";
import Text from "@ds/components/Text";
import type { TArticle, TTestimony } from "../../type";

interface ITestimony {
  description: string;
  image: {
    mediaId: string;
    mediaName: string;
    mediaPath: string;
  };
  jobTitle: string;
  name: string;
}

interface ISuccessStory {
  article: TArticle[];
  testimoni: ITestimony[];
}

const ContainerSuccessStory = ({ article, testimoni }: ISuccessStory) => {
  const now = moment().format("MMM DD, YYYY");
  const isTestimoniExist = testimoni?.length > 0;
  const isArticleExist = article?.length > 0;

  const _renderContent = () => {
    if (isTestimoniExist && !isArticleExist) {
      return (
        <Box
          display="flex"
          alignItems="center"
          sx={{
            justifyContent: testimoni?.length < 3 ? "space-around" : "space-between !important",
          }}
          flexDirection="row"
          width="100%"
          gap={2}
        >
          {testimoni?.map((el: TTestimony, idx: number) => (
            <ProductCard key={idx} title="" width={360} imageHeight={0}>
              <Box minHeight={326}>
                <Box
                  display="flex"
                  alignItems="center"
                  flexDirection="column"
                  textAlign="center"
                  mb={3}
                >
                  <Box mt={5}>
                    <img src={el.image.mediaPath} width={96} />
                  </Box>
                  <Text variant="h3" weight="700">
                    {el.name}
                  </Text>
                  <Text color="inactive-secondary" component="p" variant="body2" weight="400">
                    {el.jobTitle}
                  </Text>
                </Box>
                <Text component="p" align="center" weight="400">
                  {el.description}
                </Text>
              </Box>
            </ProductCard>
          ))}
        </Box>
      );
    } else if (isArticleExist && !isTestimoniExist) {
      return (
        <Box
          display="flex"
          alignItems={isTestimoniExist ? "flex-start" : "center"}
          justifyContent="center"
          flexDirection={isTestimoniExist ? "column" : "row"}
          gap="60px"
        >
          {article?.map((el: TArticle, idx: number) => (
            <ProductCard
              key={idx}
              image={el.image.mediaPath || ""}
              title=""
              imageHeight={200}
              height="auto"
              minHeight={328}
              width={360}
              linkTo={`/insight/${el.slug}`}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                flexDirection="column"
                minHeight={136}
              >
                <Text variant="h2" weight="600" sx={{ fontSize: 20 }}>
                  {el.title}
                </Text>
                <Text color="inactive-secondary" variant="body2" weight="400">
                  {now}
                </Text>
              </Box>
            </ProductCard>
          ))}
        </Box>
      );
    }

    return (
      <Grid container justifyContent="space-between">
        <Grid item md={6} xl={7}>
          <Box
            display="flex"
            alignItems="flex-start"
            justifyContent="center"
            flexDirection="column"
            gap={1}
          >
            {article?.map((el: TArticle, idx: number) => (
              <ProductCard
                key={idx}
                title=""
                imageHeight={0}
                height="auto"
                minHeight="auto"
                maxWidth={600}
                width="100%"
                padding="0px"
                pr={1}
                overflow="hidden"
                linkTo={`/insight/${el.slug}`}
              >
                <Box display="flex" justifyContent="flex-start" gap={2} mt="-8px">
                  <Box
                    component="div"
                    sx={{
                      background: `url("${el.image.mediaPath}") no-repeat center`,
                      backgroundSize: "cover",
                      maxWidth: 160,
                      width: "100%",
                      height: "100px",
                    }}
                  />
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="space-between"
                    pb={1}
                    pt={1}
                  >
                    <Text variant="h3" weight="600">
                      {el.title}
                    </Text>
                    <Text variant="body2" color="inactive-secondary" weight="400">
                      {now}
                    </Text>
                  </Box>
                </Box>
              </ProductCard>
            ))}
          </Box>
        </Grid>
        <Grid item md={5} xl={5}>
          <Box sx={{ ".slick-dots": { bottom: 15 } }}>
            <Testimonials
              data={
                {
                  testimonies: testimoni.map((el: ITestimony) => {
                    return {
                      image: el.image,
                      localizations: {
                        name: el.name,
                        jobTitle: el.jobTitle,
                        description: el.description,
                      },
                    };
                  }),
                } || []
              }
              minHeight={322}
            />
          </Box>
        </Grid>
      </Grid>
    );
  };

  return (
    <Grid container direction="row" justifyContent="center" alignItems="center" gap={2}>
      {_renderContent()}
    </Grid>
  );
};

export default ContainerSuccessStory;
